import {
    ActivatedRoute,
    NavigationCancel,
    NavigationEnd,
    NavigationStart,
    RouteConfigLoadStart,
    Router,
    RouterOutlet,
} from '@angular/router';
import { Component, inject, OnInit } from '@angular/core';
import { IconsService } from '@app/services/icons.service';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '@env/environment';
import { ProvidersService } from '@app/services/providers.service';
import { MomentConfig } from '@app/components/date/moment.config';
import { ContactsService } from '@app/services/contacts.service';
import { MixpanelService } from '@app/services/mixpanel.service';
import { UsersService } from '@app/services/users.service';
import { UserInterface } from '@app/interfaces/user.interface';
import { debounceTime, filter, map, mergeMap } from 'rxjs/operators';
import { datadogRum } from '@datadog/browser-rum';
import { DeviceService } from '@app/services/device.service';
import { merge, of } from 'rxjs';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { SnackbarService } from '@app/services/snackbar.service';
import { RouteMapper } from '@app/mappers/route.mapper';
import { BridgeService } from '@app/services/bridge.service';
import { LoaderService } from '@app/services/loader.service';
import { UpdateNotificationComponent } from '@app/components/app-update-notification/app-update-notification.component';
import { AppUpdateService } from '@app/services/app-update.service';
import { AppService } from '@app/services/app.service';

import version from './../../version.json';
import { MortgagesService } from '@app/services/mortgages.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [RouterOutlet, UpdateNotificationComponent],
})
export class AppComponent implements OnInit {
    protected readonly router = inject(Router);
    protected readonly route = inject(ActivatedRoute);
    protected readonly providersService = inject(ProvidersService);
    protected readonly matDialogRef = inject(MatDialog);
    protected readonly contactsService = inject(ContactsService);
    protected readonly iconsService = inject(IconsService);
    protected readonly deviceService = inject(DeviceService);
    protected readonly mixpanelService = inject(MixpanelService);
    protected readonly usersService = inject(UsersService);
    protected readonly snackbarService = inject(SnackbarService);
    protected readonly angulartics2GoogleTagManager = inject(Angulartics2GoogleTagManager);
    protected readonly mortgagesService = inject(MortgagesService);
    protected readonly loaderService = inject(LoaderService);
    protected readonly appService = inject(AppService);
    protected readonly appUpdateService = inject(AppUpdateService);
    private readonly bridgeService = inject(BridgeService);

    title = 'Hypo - Banking';
    latestRouterEvent;

    constructor() {
        this.appService.config(
            environment.production,
            'Banking',
            environment.title,
            version.buildVersion,
            environment.url.backendApi,
            environment.deploymentEnvironment
        );
        console.log('%cHYPO NA MÍRU - Banking', 'font-size:24px;color:#02c57d');
        console.log(
            `%cVersion: ${version.buildVersion}, Environment: ${environment.deploymentEnvironment}`,
            'color:#02c57d'
        );

        this.appUpdateService.setCurrentVersion(version.buildVersion);

        if (environment.deploymentEnvironment === 'production') {
            this.angulartics2GoogleTagManager.startTracking();
        }

        this.mixpanelService.init();
        this.usersService.user$.pipe(debounceTime(500)).subscribe((user: UserInterface) => {
            if (user) {
                const usr = {
                    id: user._id,
                    name: ((user.firstName ?? '') + ' ' + (user.lastName ?? '')).trim(),
                    email: user.email ?? '',
                };
                datadogRum.setUser(usr);
                this.mixpanelService.identify(user._id);
                this.mixpanelService.register({
                    isDesktop: this.deviceService.isDesktop,
                });
                let additionalData = {};
                if (user.contact) {
                    additionalData = {
                        $name: `${user.contact.lastName} ${user.contact.firstName}`,
                        'Name First': user.contact.firstName,
                        'Name Last': user.contact.lastName,
                        Sex: user.contact.sex,
                        'Phone Number': `${user.contact?.phone[0]?.countryCode}${user.contact?.phone[0]?.number}`,
                    };
                }
                this.mixpanelService.people_set({
                    $email: user.email,
                    'Sign up date': new Date(),
                    ...additionalData,
                });
                if (this.bridgeService.activateEmail) {
                    this.mixpanelService.track('$finished_activation_via_code');
                    this.bridgeService.activateEmail = false;
                }
            }
        });

        MomentConfig.config();

        merge(
            of(this.route.snapshot?.params),
            this.router.events?.pipe(
                filter((event) => event instanceof NavigationEnd),
                map((event) => {
                    this.latestRouterEvent = event;
                    return this.route;
                }),
                map((routeFromRouter) => {
                    while (routeFromRouter.firstChild) {
                        routeFromRouter = routeFromRouter.firstChild;
                    }
                    return routeFromRouter;
                }),
                filter((routeFromRouter) => routeFromRouter.outlet === 'primary'),
                mergeMap((routeFromRouter) => routeFromRouter.params)
            )
        ).subscribe((p) => {
            if (this.latestRouterEvent) {
                this.mixpanelService.track('$page_display', {
                    url: this.latestRouterEvent.url.replace(p.mortgageId, ''),
                });
            }
        });

        this.router.events?.subscribe((event) => {
            if (event instanceof NavigationStart) {
                this.loaderService.loader(true);
            }
            if (event instanceof NavigationEnd) {
                window.scrollTo(0, 0);
                this.loaderService.loader(false);
                if (RouteMapper.findDataRecursivelyFromRoute(this.route, 'disableAutoOfferRedirect')) {
                    this.mortgagesService.stopListenMessages();
                } else {
                    this.mortgagesService.startListenMessages();
                }

                const isLoginPage = event.url.indexOf('/login') === 0;
                this.appUpdateService.autoUpdate(isLoginPage);
                if (!isLoginPage) {
                    if (this.appUpdateService.updateState().available) {
                        this.appUpdateService.activateUpdate();
                    }
                }
            }
            if (event instanceof NavigationCancel) {
                this.loaderService.loader(false);
            }
            if (event instanceof RouteConfigLoadStart) {
                this.loaderService.loader(true);
            }
        });
    }

    public ngOnInit(): void {
        this.updateAppVersionTag(version.buildVersion);

        if (!environment.production) {
            document.addEventListener('keydown', (event) => {
                if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
                    event.preventDefault();
                    this.appUpdateService.simulateNewVersion(version.buildVersion);
                }
            });
        }
    }

    public updateAppVersionTag(version: string): void {
        document.querySelector('app-root').setAttribute('app-version', version);
    }
}
